import { Fragment, useEffect, useRef } from 'react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import { Route, Routes } from 'react-router-dom'
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import Navbar, { navbarHeight } from './components/layout/Navbar';
import { WorkshopProvider } from './context/workshopContext';
import { routes } from './routes';
import { sec } from './config';

const ProtectedRoute = ({ component }: { component: React.ComponentType }) => {
  const ProtectedComponent = withAuthenticationRequired(component, {
    onRedirecting: () =>
      <Backdrop
        sx={{ color: 'primary', zIndex: theme => theme.zIndex.drawer + 1, backgroundColor: 'white' }}
        open={true}
      >
        <Box sx={{ display: 'block', textAlign: 'center' }}>
          <CircularProgress color='primary' />
        </Box>
      </Backdrop>
  })
  return <ProtectedComponent />
}

const App = () => {
  const auth0 = useAuth0()

  const { getAccessTokenSilently, loginWithRedirect } = auth0

  useEffect(() => {
    sec.setAccessTokenSilently(getAccessTokenSilently)
  }, [getAccessTokenSilently])

  // The refresh token can expire while the cached user keeps isAuthenticated true,
  // so send the user to login once instead of letting every request fail silently
  const isRedirectingToLogin = useRef(false)
  useEffect(() => {
    sec.setSessionExpiredHandler(() => {
      if (isRedirectingToLogin.current) return
      isRedirectingToLogin.current = true
      loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search }
      }).catch((e) => {
        isRedirectingToLogin.current = false
        console.error('error logging in: ', e)
      })
    })
  }, [loginWithRedirect])

  return (
    <WorkshopProvider>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        width: '100%',
      }}>
        {auth0.isAuthenticated && <Navbar />}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflow: 'auto',
          pt: auth0.isAuthenticated ? `${navbarHeight}px` : 0,
          boxSizing: 'border-box',
        }}>
          <Routes>
            {routes.map((route) => {
              const isAllowedToSee = route.public || auth0.isAuthenticated
              return <Route
                path={route.url}
                element={route.public ? <route.component /> : <ProtectedRoute component={isAllowedToSee ? route.component : Fragment} />}
                key={route.url} />
            })
            }
          </Routes>
        </Box>
      </Box>
    </WorkshopProvider>
  )
}

export default App;
